import { Params } from '@angular/router';
import {
  ActivityStatusEnum,
  SortEnum,
  TimeslotStatusEnum,
} from '@sport-activities/sdk';
import { ActivityFilterParamsInterface } from '~/attendee/interfaces/activity-filter-params.interface';
import { deepMerge } from '~/core/helpers/object/deep-merge/deep-merge.helper';

import { configuredLang } from '~/i18n/i18n.module';
import { WidgetParametersInterface } from '~/widget/interfaces/widget-parameters.interface';

const MAX_DISTANCE = 1000;
const MAX_LIMIT = 10;
const MAX_PER_PAGE = 6;

export const DEFAULT_PARAMETERS: WidgetParametersInterface = {
  slider: true,
  perPage: 4,
  limit: MAX_LIMIT,
};

/**
 * Transforms raw query params into structured widget paramters object.
 */
export function buildParametersObject(
  params: Params,
  acceptLangs: string[] = configuredLang
): WidgetParametersInterface {
  const {
    latitude,
    longitude,
    distance,
    sport,
    slider,
    perpage, // small case (from v1)
    limit,
    organizer,
    group,
    lang,
  } = params;

  const widgetParameters = {
    ...DEFAULT_PARAMETERS,
  };

  if (latitude && longitude) {
    widgetParameters.latitude = latitude;
    widgetParameters.longitude = longitude;
  }

  if (distance) {
    widgetParameters.distance =
      distance <= MAX_DISTANCE ? distance : MAX_DISTANCE;
  }

  if (sport) {
    widgetParameters.sports = sport
      .split(',')
      .map((stringSport: string) => Number(stringSport));
  }

  if (slider !== undefined) {
    widgetParameters.slider = slider;
  }

  if (perpage) {
    widgetParameters.perPage = getPerpageParam(perpage);
  }

  if (limit) {
    widgetParameters.limit = getLimitParam(limit);
  }

  if (organizer) {
    widgetParameters.organizer = organizer;
  }

  if (group) {
    widgetParameters.groups = group
      .split(',')
      .map((stringSport: string) => Number(stringSport));
  }

  if (lang) {
    widgetParameters.langs = lang
      .split(',')
      .filter((widgetLang: string) => acceptLangs.includes(widgetLang));
  }

  return widgetParameters;
}

/**
 * Build activity filter (for API calls) from widget params
 */
export function buildActivityFilterObject(
  widgetParams: WidgetParametersInterface,
  includeNextDatesEnabled: boolean
): ActivityFilterParamsInterface {
  const { distance, latitude, longitude, limit, organizer, sports, groups } =
    widgetParams;

  let filters: ActivityFilterParamsInterface = {
    geo: {
      distance,
      latitude,
      longitude,
    },
    pagination: {
      limit,
    },
    sort: {
      by: 'timeslots.startDate',
      order: 'asc' as SortEnum,
    },
    organizationIdentifier: organizer,
    sports,
    status: 'PUBLISHED' as ActivityStatusEnum,
    timeslots: {
      startDate: new Date().toISOString(),
      excludeFull: true,
      timeslotStatus: 'PUBLISHED' as TimeslotStatusEnum,
      groups,
    },
  };

  if (includeNextDatesEnabled) {
    filters = deepMerge(filters, {
      includeNextDates: true,
    });
  }
  return filters;
}

export function getLimitParam(limit) {
  return limit <= MAX_LIMIT ? limit : MAX_LIMIT;
}

export function getPerpageParam(perpage) {
  return perpage <= MAX_PER_PAGE ? perpage : MAX_PER_PAGE;
}
